<script setup lang="ts">
import { TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import {
  EVENT_MEDIA_MODES,
  type EventMediaMode,
} from '~/shared/lib/events-routes.const';

defineProps<{
  mode: EventMediaMode;
  year: number;
  canPrev: boolean;
  canNext: boolean;
}>();

const emit = defineEmits<{
  'update:mode': [mode: EventMediaMode];
  'prev-year': [];
  'next-year': [];
}>();

const MEDIA_MODE_VALUES = Object.values(EVENT_MEDIA_MODES) as EventMediaMode[];

function updateMode(value: string | number): void {
  if (typeof value !== 'string') {
    return;
  }
  const next = MEDIA_MODE_VALUES.find((mode) => mode === value);
  if (next) {
    emit('update:mode', next);
  }
}
</script>

<template>
  <header :class="$style.header">
    <div :class="$style.yearNav">
      <button
        type="button"
        :class="$style.yearNav__btn"
        :disabled="!canPrev"
        aria-label="Предыдущий год"
        @click="emit('prev-year')"
      >
        <Icon name="ic:baseline-chevron-left" size="24" />
      </button>
      <span :class="$style.yearNav__value">{{ year }}</span>
      <button
        type="button"
        :class="$style.yearNav__btn"
        :disabled="!canNext"
        aria-label="Следующий год"
        @click="emit('next-year')"
      >
        <Icon name="ic:baseline-chevron-right" size="24" />
      </button>
    </div>

    <TabsRoot
      :model-value="mode"
      :class="$style.modeTabs"
      @update:model-value="updateMode"
    >
      <TabsList :class="$style.modeTabs__list">
        <TabsTrigger :value="EVENT_MEDIA_MODES.photos" :class="$style.modeTab">
          <Icon
            name="ic:baseline-photo-camera"
            size="20"
            :class="$style.modeTab__icon"
          />
          <span>Фото</span>
        </TabsTrigger>
        <TabsTrigger :value="EVENT_MEDIA_MODES.videos" :class="$style.modeTab">
          <Icon
            name="ic:baseline-smart-display"
            size="20"
            :class="$style.modeTab__icon"
          />
          <span>Видео</span>
        </TabsTrigger>
      </TabsList>
    </TabsRoot>
  </header>
</template>

<style module lang="scss">
.header {
  --gallery-header-height: 64px;
  --gallery-tabs-height: var(--gallery-header-height);
  --gallery-tab-width: 135px;
  --gallery-tab-padding: 16px 40px;

  display: flex;
  align-items: stretch;
  justify-content: space-between;
  min-height: var(--gallery-header-height);
  padding-left: 20px;
  border-bottom: 1px solid var(--color-gray-line);
}

.yearNav {
  display: flex;
  align-items: center;
  gap: var(--space-1x);
}

.yearNav__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  color: var(--color-black-1);
  background: none;
  border: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.yearNav__value {
  @include text_h5;
  color: var(--color-black-1);
  min-width: 48px;
  text-align: center;
}

.modeTabs {
  display: flex;
  flex-shrink: 0;
  width: calc(var(--gallery-tab-width) * 2);
  height: var(--gallery-tabs-height);
}

.modeTabs__list {
  display: flex;
  width: 100%;
  height: 100%;
}

.modeTab {
  @include text_caption1_bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-0-5x);
  box-sizing: border-box;
  flex: 0 0 var(--gallery-tab-width);
  height: 100%;
  padding: var(--gallery-tab-padding);
  color: var(--color-black-2);
  background-color: var(--color-white);
  border: none;
  border-left: 1px solid var(--color-gray-line);
  cursor: pointer;
  overflow: hidden;
  transition:
    background-color 0.2s,
    color 0.2s;

  &:hover {
    color: var(--color-red-1);
  }

  &[data-state='active'] {
    color: var(--color-red-1);
    background-color: var(--color-gray-bg);
  }
}

.modeTab__icon {
  width: 20px;
  height: 20px;
  font-size: 20px;
  flex-shrink: 0;
}

@media (max-width: 599px) {
  .header {
    --gallery-header-height: 60px;
    --gallery-tabs-height: 36px;
    --gallery-tab-padding: 8px 40px;

    align-items: center;
    box-sizing: border-box;
  }

  .yearNav {
    min-height: 40px;
  }

  .modeTab {
    border-top: 1px solid var(--color-gray-line);
    border-bottom: 1px solid var(--color-gray-line);
  }
}

@media (max-width: 478px) {
  .header {
    --gallery-tab-width: 96px;
    --gallery-tab-padding: 8px 20px;
  }
}
</style>
